import { Controller, get } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './shadcn-ui/select';
import { Textarea } from './shadcn-ui/textarea';
import { Input } from './shadcn-ui/input';
import { cn } from '@/lib/utils';
import { Label } from './shadcn-ui/label';
import { DateTimePicker } from './date-time-picker';
import { CircleAlert } from 'lucide-react';
import { DatePicker } from './date-picker';

const FormField = ({
  datetime,
  date,
  label,
  name,
  control,
  register,
  errors,
  type = 'text',
  textarea = false,
  select = false,
  options = [],
  placeholder,
  disabled = false,
  step,
  inputMode,
  required,
  className,
}) => {
  // const error = errors?.[name];
  const error = get(errors, name);

  // ================= SELECT =================
  if (select) {
    if (!control) {
      console.error(`FormField "${name}" requires control for Select`);
      return null;
    }

    return (
      <div className="space-y-1">
        <Label className="text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
          {label}
          {required && <span className="relative -top-0.5 ml-0.5">*</span>}
        </Label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            // Convert boolean to string for Select component
            const getStringValue = val => {
              if (val === true) return 'true';
              if (val === false) return 'false';
              if (val === null || val === undefined) return '';
              return String(val);
            };

            // Convert string back to original type
            const handleValueChange = val => {
              // Check if any option has a boolean value
              const hasBooleanOption = options.some(opt => typeof opt.value === 'boolean');
              if (hasBooleanOption) {
                // Convert string back to boolean
                field.onChange(val === 'true');
              } else {
                // For string/number values, pass as-is (Select already provides the string value)
                field.onChange(val);
              }
            };

            return (
              <Select value={getStringValue(field.value)} onValueChange={handleValueChange} disabled={disabled}>
                <SelectTrigger
                  className={cn('rounded-[10px] bg-white text-sm font-normal', error && 'border-red-500', className)}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>

                <SelectContent>
                  {options.map(opt => {
                    // Convert option value to string for Select component
                    const stringValue = typeof opt.value === 'boolean' ? String(opt.value) : String(opt.value);
                    return (
                      <SelectItem key={stringValue} value={stringValue}>
                        {opt.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            );
          }}
        />
        {error && <div className="text-xs text-red-500">{error.message}</div>}
      </div>
    );
  }

  // ================= TEXTAREA =================
  if (textarea) {
    if (!register) {
      console.error(`FormField "${name}" requires register for Textarea`);
      return null;
    }

    return (
      <div className="space-y-1">
        <Label className="text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
          {label}
          {required && <span className="relative -top-0.5 ml-0.5">*</span>}
        </Label>
        <Textarea
          disabled={disabled}
          placeholder={placeholder}
          className={cn('rounded-[10px] bg-white text-sm font-normal', error && 'border-red-500', className)}
          {...register(name)}
        />
        {error && <div className="text-xs text-red-500">{error.message}</div>}
      </div>
    );
  }

  if (datetime) {
    if (!control) {
      console.error(`FormField "${name}" requires register for Textarea`);
      return null;
    }

    return (
      <div className="space-y-1">
        <Label className="text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
          {label}
          {required && <span className="relative -top-0.5 ml-0.5">*</span>}
        </Label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <DateTimePicker
              className={'overflow-x-hidden'}
              value={field.value ?? null}
              onChange={field.onChange}
              disabled={disabled}
              error={error}
            />
          )}
        />
        {error && <div className="text-xs text-red-500">{error.message}</div>}
      </div>
    );
  }

  if (date) {
    if (!control) {
      console.error(`FormField "${name}" requires control for DatePicker`);
      return null;
    }

    return (
      <div className="space-y-1">
        <Label className="text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
          {label}
          {required && <span className="relative -top-0.5 ml-0.5">*</span>}
        </Label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            // Ensure the value is a Date object or null
            const dateValue = field.value instanceof Date ? field.value : field.value ? new Date(field.value) : null;

            return <DatePicker value={dateValue} onChange={field.onChange} bgColor="bg-white" />;
          }}
        />
        {error && <div className="text-xs text-red-500">{error.message}</div>}
      </div>
    );
  }

  // ================= INPUT =================
  if (!register) {
    console.error(`FormField "${name}" requires register for Input`);
    return null;
  }

  return (
    <div className="space-y-1">
      <Label className="text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
        {label}
        {required && <span className="relative -top-0.5 ml-0.5">*</span>}
      </Label>

      <div className="relative">
        <Input
          disabled={disabled}
          type={type}
          placeholder={placeholder}
          step={step}
          inputMode={inputMode}
          className={cn('rounded-[10px] bg-white text-sm font-normal', error && 'border-red-500', className)}
          {...register(
            name,
            type === 'number'
              ? {
                  setValueAs: value => {
                    // Convert empty string to null, NaN to null, otherwise convert to number
                    if (value === '' || value === null || value === undefined) return null;
                    const num = Number(value);
                    return Number.isNaN(num) ? null : num;
                  },
                }
              : {}
          )}
        />
        {error && <CircleAlert size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />}
      </div>
      {error && <div className="text-xs text-red-500">{error.message}</div>}
    </div>
  );
};

export default FormField;
